import {
  UNASSIGNED,
  UNASSIGNED_FILTER_VALUE,
} from './ZoneLevelLocationConstants'

/**
 * Single source of truth for alphanumeric label ordering across the project's
 * filter, group, and sort option lists.
 *
 * Uses `numeric: true` so `EQP-2` precedes `EQP-10`, and `sensitivity: 'base'`
 * so case/diacritics don't reorder otherwise-equal labels. The collator is
 * constructed once at module scope and reused on every comparison.
 */
const alphanumericCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

/**
 * Compare two already-resolved labels alphanumerically. Use this for option
 * lists that have no special "unassigned" bucket (group/sort field lists,
 * picker value lists where the unassigned entry is handled separately).
 */
export function compareLabelsAlphanumeric(
  aLabel: string,
  bLabel: string,
): number {
  return alphanumericCollator.compare(aLabel, bLabel)
}

export function compareLabelPaths(a: string[], b: string[]): number {
  const shared = Math.min(a.length, b.length)
  for (let i = 0; i < shared; i++) {
    const comparison = compareLabelsAlphanumeric(a[i], b[i])
    if (comparison !== 0) return comparison
  }
  return a.length - b.length
}

/**
 * Default group ordering for grouped lists/tables: alphanumeric by displayed
 * label, with the unassigned bucket pinned last. Delegates to
 * `compareLabelsAlphanumeric` for the non-unassigned comparison.
 */
export function compareGroupsByLabel(
  aKey: string,
  bKey: string,
  resolveLabel: (key: string) => string,
): number {
  if (aKey === UNASSIGNED_FILTER_VALUE) return 1
  if (bKey === UNASSIGNED_FILTER_VALUE) return -1
  return compareLabelsAlphanumeric(resolveLabel(aKey), resolveLabel(bKey))
}

/**
 * Group ordering for the list selectors, whose group key IS the display label
 * and whose unassigned bucket is the `UNASSIGNED` label rather than the
 * `UNASSIGNED_FILTER_VALUE` sentinel `compareGroupsByLabel` pins.
 *
 * Note the plain `localeCompare` rather than `compareLabelsAlphanumeric`: this
 * is a verbatim consolidation of the three inline comparators the selectors
 * already shipped, so it deliberately preserves their (non-numeric) ordering.
 * Switching it would reorder numbered categories in every picker — worth doing,
 * but as its own change, not smuggled in with the de-duplication.
 */
export function compareGroupLabelsUnassignedLast(a: string, b: string): number {
  if (a === UNASSIGNED) return 1
  if (b === UNASSIGNED) return -1
  return a.localeCompare(b)
}
